import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LanguageProvider } from './hooks/useTranslation';
import Dashboard from './components/Dashboard';
import AddTransaction from './components/AddTransaction';
import TransactionList from './components/TransactionList';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import './App.css';

// Budget data context
const BudgetContext = React.createContext();

export const useBudget = () => {
  const context = React.useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within BudgetProvider');
  }
  return context;
};

const BudgetProvider = ({ children }) => {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([
    'food', 'transport', 'bills', 'entertainment', 'rent', 'health', 'other',
    'salary', 'business', 'investment'
  ]);
  const [currency, setCurrency] = useState('YER');

  // Load data from localStorage
  useEffect(() => {
    const savedTransactions = localStorage.getItem('budget_transactions');
    const savedCategories = localStorage.getItem('budget_categories');
    const savedCurrency = localStorage.getItem('budget_currency');

    if (savedTransactions) {
      setTransactions(JSON.parse(savedTransactions));
    }
    if (savedCategories) {
      setCategories(JSON.parse(savedCategories));
    }
    if (savedCurrency) {
      setCurrency(savedCurrency);
    }
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('budget_transactions', JSON.stringify(transactions));
  }, [transactions]);

  useEffect(() => {
    localStorage.setItem('budget_categories', JSON.stringify(categories));
  }, [categories]);

  useEffect(() => {
    localStorage.setItem('budget_currency', currency);
  }, [currency]);

  const addTransaction = (transaction) => {
    const newTransaction = {
      ...transaction,
      id: Date.now().toString(),
      date: transaction.date || new Date().toISOString().split('T')[0]
    };
    setTransactions(prev => [newTransaction, ...prev]);
  };

  const deleteTransaction = (id) => {
    setTransactions(prev => prev.filter(t => t.id !== id));
  };

  const updateTransaction = (id, updatedTransaction) => {
    setTransactions(prev => 
      prev.map(t => t.id === id ? { ...t, ...updatedTransaction } : t)
    );
  };

  const addCategory = (category) => {
    if (!categories.includes(category)) {
      setCategories(prev => [...prev, category]);
    }
  };

  // Calculate totals
  const currentMonth = new Date().toISOString().substring(0, 7);
  const monthlyTransactions = transactions.filter(t => 
    t.date.startsWith(currentMonth)
  );

  const totalIncome = monthlyTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);

  const totalExpenses = monthlyTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);

  const balance = totalIncome - totalExpenses;

  return (
    <BudgetContext.Provider value={{
      transactions,
      categories,
      currency,
      addTransaction,
      deleteTransaction,
      updateTransaction,
      addCategory,
      setCurrency,
      monthlyTransactions,
      totalIncome,
      totalExpenses,
      balance
    }}>
      {children}
    </BudgetContext.Provider>
  );
};

function App() {
  return (
    <LanguageProvider>
      <BudgetProvider>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
          <BrowserRouter>
            <Navigation />
            <main className="container mx-auto px-4 py-6 min-h-screen">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/add" element={<AddTransaction />} />
                <Route path="/transactions" element={<TransactionList />} />
              </Routes>
            </main>
            <Footer />
          </BrowserRouter>
        </div>
      </BudgetProvider>
    </LanguageProvider>
  );
}

export default App;